import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getWorkflowEditChoice } from "../../../api/content/versionsApi";
import type { WorkflowEdit } from "../../../api/content/versionDecoders";
import { useLoad } from "../../../shared/loading/useLoad";
import AppButton from "../../../ui/buttons/AppButton";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import { FORM_HELP_TEXT_CLASS } from "../../../ui/styles/formStyles";
import { workflowEditActions } from "./workflowEditActions";

interface WorkflowEditButtonsProps {
  projectId: string;
  /** The Workflow version the edit starts from, `<item id>@<blob>`. */
  workflow: string;
  workflowName: string;
  /** The change set holding the edit, once there is one: its plan refines the default. */
  changeSetId?: string | null;
  onChoose: (edit: WorkflowEdit) => void;
  disabled?: boolean;
}

/**
 * Update the Workflow or save a new one derived from it, as two named buttons (F33,
 * ADR-0033 D4). The default is the primary button; updating a library's Workflow is
 * disabled, with the reason. What each choice does is the caller's.
 */
export default function WorkflowEditButtons({ projectId, workflow, workflowName, changeSetId = null, onChoose, disabled = false }: WorkflowEditButtonsProps) {
  const { t } = useTranslation();
  const load = useCallback((signal: AbortSignal) => getWorkflowEditChoice(projectId, workflow, changeSetId, signal), [projectId, workflow, changeSetId]);
  const { state, reload } = useLoad(load);
  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("versions.edit.loading")} onRetry={reload} compact={true} />;
  const actions = workflowEditActions(t, state.value, workflowName);
  const unavailable = actions.find((action) => action.unavailable !== null)?.unavailable ?? null;
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap justify-end gap-2">
        {actions.map((action) => (
          <AppButton
            key={action.edit}
            size="sm"
            variant={action.primary ? "primary" : "secondary"}
            disabled={disabled || action.unavailable !== null}
            title={action.unavailable === null ? undefined : t(`versions.edit.unavailable.${action.unavailable}`)}
            onClick={() => {
              onChoose(action.edit);
            }}
          >
            {action.text}
          </AppButton>
        ))}
      </div>
      {unavailable === null ? null : <p className={`${FORM_HELP_TEXT_CLASS} text-right`}>{t(`versions.edit.unavailable.${unavailable}`)}</p>}
      {state.value.mayChangeMethod ? <p className={`${FORM_HELP_TEXT_CLASS} text-right`}>{t("versions.merge.mayChangeMethod")}</p> : null}
    </div>
  );
}
